import Button from '@atoms/Button/Button';
import Checkbox from '@atoms/Checkbox/Checkbox';
import Text from '@atoms/Text/Text';
import TextInput from '@atoms/TextInput/TextInput';
import SiteEnum from '@models/enums/SiteEnum';
import IFormValues from '@models/IFormValues';
import { IMeta } from '@models/IMeta';
import { fadeInUp } from '@utility/config/animations';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

export interface INewsletterSignup {
  readonly consentText: string;
  readonly consentValidationMessage: string;
  readonly country: string;
  readonly emailPlaceholder: string;
  readonly errorMessage: string;
  readonly errorStateMessage: string;
  readonly language: string;
  readonly meta?: IMeta;
  readonly site?: SiteEnum;
  readonly source: string;
  readonly subscribeCtaLabel: string;
  readonly subscribersEndpoint: string;
  readonly successMessage: string;
  readonly title: string;
}

const NewsletterSignup = ({
  consentText,
  consentValidationMessage, // Not currently required in the spec
  emailPlaceholder,
  errorMessage,
  errorStateMessage,
  site,
  source,
  subscribeCtaLabel,
  subscribersEndpoint,
  successMessage,
  title,
  country,
  language,
}: INewsletterSignup) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setIsLoading] = useState(false);

  const formMethods = useForm<IFormValues>({
    defaultValues: {
      email: '',
      consent: false,
    },
  });
  const { register, watch, reset, setError, handleSubmit } = formMethods;
  const consent = watch('consent');

  const onSubmit: SubmitHandler<IFormValues> = async (data) => {
    setIsLoading(true);
    try {
      if (!subscribersEndpoint) throw new Error('no endpoint setup');

      const requestBody = {
        consent: data.consent,
        country,
        language,
        source,
        subscriber: {
          email: data.email,
        },
      };

      const response = await fetch(subscribersEndpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: { 'Content-Type': 'application/json' },
      });

      const responseData = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        reset();

        window.dataLayer.push({
          event: 'trackingEvent_sign_up_newsletter',
          event_name_variable: 'sign_up_newsletter',
        });
      } else {
        setError('email', {
          type: 'server',
          message: responseData?.error || errorStateMessage,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="tw-bg-white tw-text-black dark:tw-bg-grey-10">
      <div className="tw-grid-container tw-gap-md tw-py-xl md:tw-py-2xl">
        <div className="tw-col-span-full lg:tw-col-span-6">
          <Text variant="headline">{title}</Text>
        </div>
        <div className="tw-col-span-full lg:tw-col-span-6">
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              <motion.div
                variants={{ ...fadeInUp, hidden: { y: 20 } }}
                animate="visible"
                initial="hidden"
                className="tw-flex tw-h-full tw-items-center"
              >
                <Text variant="body">{successMessage}</Text>
              </motion.div>
            ) : (
              <FormProvider {...formMethods}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="tw-flex tw-flex-col tw-gap-md"
                >
                  <div className="tw-flex tw-items-start tw-gap-xs [&>div]:tw-flex-1">
                    <TextInput
                      placeholder={emailPlaceholder}
                      registerOptions={{
                        required: errorMessage,
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/i,
                          message: errorMessage,
                        },
                      }}
                      id="email"
                      label="Email"
                      type="email"
                      displayLabel={false}
                    />
                    <Button
                      variant="yellow"
                      label={subscribeCtaLabel}
                      type="submit"
                      disabled={!consent || loading}
                    />
                  </div>
                  <Checkbox
                    register={register}
                    name="consent"
                    label={consentText}
                    richTextLabel
                  />
                </form>
              </FormProvider>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
